import { apiFetch } from "@shared/api-client";
import type { PackageFormData } from "./types";
import type { InquiryProfileConfig } from "./inquiry-profile-schema";
import { estimateInquiryFormData } from "./profile-state";
import { withAttendanceFromBand } from "./submit-inquiry";

/**
 * The estimate, asked for once: POST /api/inquiry/estimate with the enquiry
 * as the form holds it when the Customer leaves the last content step. The
 * server has the model choose gear from the Company's catalogue and prices
 * it, so every ask is a model call — which is why nothing asks as the
 * Customer types. Every failure resolves to null and the form carries on
 * to contact: the estimate is an enhancement, never a blocker.
 */

export interface EstimateRange {
  low: number;
  high: number;
  currency: string;
  disclaimer: string;
}

export type EstimateState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; range: EstimateRange };

interface EstimateRequestBody {
  profileKey: string;
  form: Partial<PackageFormData>;
}

/** What the server is sent: the answers the Customer can see, the
 *  attendance band resolved to a number as the submission resolves it,
 *  and nothing about who they are. */
export function buildEstimateBody(
  profile: InquiryProfileConfig,
  formData: Partial<PackageFormData>,
  riderAttached: boolean,
): EstimateRequestBody {
  const form = withAttendanceFromBand(
    estimateInquiryFormData(profile, formData, riderAttached),
  );
  return { profileKey: profile.key, form };
}

export async function requestEstimate(
  profile: InquiryProfileConfig,
  formData: Partial<PackageFormData>,
  riderAttached: boolean,
): Promise<EstimateRange | null> {
  try {
    const res = await apiFetch<{
      enabled: boolean;
      currency?: string;
      low?: number;
      high?: number;
      disclaimer?: string;
    }>("/api/inquiry/estimate", {
      method: "POST",
      body: buildEstimateBody(profile, formData, riderAttached),
    });
    if (!res.enabled || res.low === undefined || res.high === undefined) {
      return null;
    }
    return {
      low: res.low,
      high: res.high,
      currency: res.currency ?? "NZD",
      disclaimer: res.disclaimer ?? "",
    };
  } catch {
    return null;
  }
}

/** `$950 – $1,600` (whole dollars, locale separators). */
export function formatEstimateRange(range: EstimateRange): string {
  const f = (n: number): string => `$${Math.round(n).toLocaleString()}`;
  return `${f(range.low)} – ${f(range.high)}`;
}
